import { flatten, removeNullUndefined } from "@nens/shared-utils";

import { MetaModel } from "../endpoints/meta-model";
import { ApiScenario, ScenarioStatus } from "../endpoints/scenarios";

const EXPORTABLE_STATUSES: ScenarioStatus[] = [
  "import_checked",
  "quality_control_requested",
  "quality_rejected",
  "quality_checked",
];

export class Scenario {
  private readonly scenario: ApiScenario;
  private readonly metaModel: MetaModel;

  constructor(scenario: ApiScenario, metaModel: MetaModel) {
    this.scenario = scenario;
    this.metaModel = metaModel;
  }

  get name(): string {
    return this.scenario.name;
  }
  get id(): number {
    return this.scenario.id;
  }
  get tenantId(): number {
    return this.scenario.tenant;
  }
  get breaches() {
    return this.scenario.breaches;
  }
  get status() {
    return this.scenario.status;
  }
  get rasters() {
    return this.scenario.rasters;
  }
  get files() {
    return this.scenario.files;
  }
  get createdAt() {
    return this.scenario.created_at;
  }
  get meta() {
    return this.scenario.meta;
  }
  get owner(): string | null {
    // This works in current meta models, may need updating in the future
    const owner = this.scenario.meta["eigenaar overstromingsinformatie"];
    return typeof owner === "string" && owner ? owner : null;
  }
  get floodingFrequency(): number | null {
    // This works in current meta models, may need updating in the future
    const frequency = this.scenario.meta["overschrijdingsfrequentie"];
    return typeof frequency === "number" ? frequency : null;
  }
  get errors() {
    return this.scenario.errors;
  }
  get canBeExported() {
    return EXPORTABLE_STATUSES.includes(this.status);
  }
  get metadataId() {
    return this.metaModel.id;
  }

  get metadataHeaders() {
    return this.metaModel.json_schemas.map((schema) => schema.title);
  }

  get numFilledInMetadataProperties() {
    return removeNullUndefined(Object.values(this.scenario.meta)).length;
  }

  getPropertiesByHeader(header: string) {
    const jsonSchema = this.metaModel.json_schemas.find((schema) => schema.title === header);

    if (!jsonSchema) throw new Error(`JSON Schema not found: ${header}`);

    const properties = jsonSchema.properties;

    return properties.map((property) => {
      const value = this.scenario.meta[property.key] ?? null;
      const filledIn = value !== null;
      const en = property.i18n?.en ?? null;
      return {
        type: property.type,
        key: property.key,
        text: {
          nl: {
            title: property.title,
            description: property.description,
          },
          en: {
            // Use english versions if configured, otherwise defaults (dutch)
            title: en?.title ?? property.title,
            description: en?.description ?? property.description,
          },
        },
        filledIn,
        value,
      };
    });
  }

  getAllPropertyKeysAndTitles(): Record<string, string> {
    const keysAndTitles = removeNullUndefined(
      flatten(
        this.metadataHeaders.map((header) =>
          this.metaModel.json_schemas
            .find((schema) => schema.title === header)
            ?.properties.map((prop) => ({ key: prop.key, title: prop.title }))
        )
      )
    );
    return Object.fromEntries(
      keysAndTitles.map((keyAndTitle) => [keyAndTitle.key, keyAndTitle.title])
    );
  }
}
